class SubscriptionPickerComponent extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  form = null;
  /** @type {HTMLInputElement | null} */
  sellingPlanInput = null;
  /** @type {HTMLInputElement | null} */
  quantityInput = null;
  ownsQuantityInput = false;

  connectedCallback() {
    const formId = this.dataset.formId;
    this.form = formId
      ? /** @type {HTMLFormElement | null} */ (document.getElementById(formId))
      : null;
    if (!this.form) {
      const section = this.closest('.shopify-section');
      this.form = /** @type {HTMLFormElement | null} */ (
        section?.querySelector('form[id^="BuyButtons-ProductForm-"]') ?? null
      );
    }
    if (!this.form) return;

    this.sellingPlanInput = this.#ensureHiddenInput('selling_plan');

    const existingQuantityInput = /** @type {HTMLInputElement | null} */ (
      this.form.querySelector('input[name="quantity"]:not([data-subscription-picker])')
    );
    this.ownsQuantityInput = !existingQuantityInput;
    this.quantityInput = this.ownsQuantityInput
      ? this.#ensureHiddenInput('quantity')
      : existingQuantityInput;

    this.addEventListener('change', this.#handleChange);
    this.applySelected();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
  }

  /**
   * @param {string} name
   * @returns {HTMLInputElement}
   */
  #ensureHiddenInput(name) {
    const form = /** @type {HTMLFormElement} */ (this.form);
    let input = /** @type {HTMLInputElement | null} */ (
      form.querySelector(`input[name="${name}"][data-subscription-picker]`)
    );
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.setAttribute('data-subscription-picker', '');
      form.appendChild(input);
    }
    return input;
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.classList.contains('subscription-picker__radio')) {
      this.applySelected();
    }
  };

  applySelected() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('.subscription-picker__radio:checked')
    );
    if (!checked || !this.form || !this.sellingPlanInput) return;

    const planId = checked.dataset.planId || '';
    const qty = Math.max(1, parseInt(checked.dataset.quantity || '1', 10) || 1);

    this.sellingPlanInput.value = planId;

    const quantitySelector = /** @type {any} */ (
      this.form.querySelector('quantity-selector-component')
    );
    if (quantitySelector?.setValue) {
      quantitySelector.setValue(qty);
    } else if (this.quantityInput) {
      this.quantityInput.value = String(qty);
    }

    this.querySelectorAll('.subscription-picker__option').forEach(
      /** @param {Element} label */ (label) => {
        const radio = label.querySelector('.subscription-picker__radio');
        /** @type {HTMLElement} */ (label).dataset.checked = radio === checked ? 'true' : 'false';
      }
    );

    this.#updateProductPrice(
      checked.dataset.priceHtml,
      checked.dataset.compareHtml,
      checked.dataset.isSubscription === 'true',
      checked.dataset.unitLabel
    );
  }

  /**
   * @param {string | undefined} priceHtml
   * @param {string | undefined} compareHtml
   * @param {boolean} isSubscription
   * @param {string | undefined} unitLabel
   */
  #updateProductPrice(priceHtml, compareHtml, isSubscription, unitLabel) {
    if (!priceHtml) return;
    const section = this.closest('.shopify-section');
    if (!section) return;
    const containers = section.querySelectorAll(
      'product-price [ref="priceContainer"], .sticky-add-to-cart__price [ref="priceContainer"]'
    );
    containers.forEach(
      /** @param {Element} container */ (container) => {
        container.innerHTML = this.#buildPriceMarkup(priceHtml, compareHtml, isSubscription, unitLabel);
      }
    );
  }

  /**
   * @param {string} priceHtml
   * @param {string | undefined} compareHtml
   * @param {boolean} isSubscription
   * @param {string | undefined} unitLabel
   * @returns {string}
   */
  #buildPriceMarkup(priceHtml, compareHtml, isSubscription, unitLabel) {
    const label = unitLabel ?? this.dataset.unitLabel ?? '/ tin';
    const safeUnitLabel = label
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    if (isSubscription && compareHtml) {
      return `
        <div class="price__sale subscription-picker__price-inline">
          <span class="price-item__group">
            <span class="price-item--sale price">${priceHtml}</span>
          </span>
          <span class="price-item__group">
            <span class="price-item--regular compare-at-price">${compareHtml}</span>
          </span>
        </div>
        <small class="subscription-picker__price-note">${safeUnitLabel}</small>
      `;
    }
    return `
      <div class="price__regular">
        <span class="price">${priceHtml}</span>
      </div>
      <small class="subscription-picker__price-note">${safeUnitLabel}</small>
    `;
  }
}

if (!customElements.get('subscription-picker-component')) {
  customElements.define('subscription-picker-component', SubscriptionPickerComponent);
}
